/**
 * The event of a conference that has none (BACKLOG 111).
 *
 * New conference makes a site and its one event together (D-44), but a site
 * declared in code is synced as a site alone, so on a fresh database every such
 * conference opened onto "This conference has no event" and nothing to press.
 * This is New conference's second half: the name, and the days, with the slug
 * the start year as New conference makes it. Everything else starts at the
 * server's defaults and is changed in the conference's own settings.
 */

import { type FormEvent, useId, useState } from "react";

import { Button, FieldShell, TextInput } from "@/components/ui/controls";
import { Notice, ProblemSummary } from "@/components/ui/states";
import { ApiError, api, ValidationError } from "@/lib/api";
import { formatDay, parseDay } from "@/lib/events";
import type { EventRecord, FieldProblem } from "@/lib/types";

interface Draft {
  name: string;
  starts_on: string;
  ends_on: string;
}

const LABELS = {
  name: "Name",
  starts_on: "Starts",
  ends_on: "Ends",
  slug: "Starts",
} as const;

function labelFor(name: string): string | undefined {
  return Object.hasOwn(LABELS, name) ? LABELS[name as keyof typeof LABELS] : undefined;
}

export function validate(draft: Draft): FieldProblem[] {
  const problems: FieldProblem[] = [];
  const problem = (field: keyof Draft, message: string) =>
    problems.push({ path: [field], message });
  if (!draft.name) problem("name", "An event needs a name.");
  const starts = parseDay(draft.starts_on);
  if (!starts) problem("starts_on", "Pick the day the event starts.");
  if (!parseDay(draft.ends_on)) problem("ends_on", "Pick the day the event ends.");
  else if (starts && draft.ends_on < draft.starts_on) {
    problem("ends_on", "An event cannot end before it starts.");
  }
  return problems;
}

interface FirstEventFormProps {
  site: string;
  /** The conference's own name, which its event usually shares. */
  siteName?: string;
  onCreated: (event: EventRecord) => Promise<void> | void;
}

export function FirstEventForm({ site, siteName = "", onCreated }: FirstEventFormProps) {
  const uid = useId();
  const today = formatDay(new Date());
  const [draft, setDraft] = useState<Draft>({
    name: siteName,
    starts_on: today,
    ends_on: today,
  });
  const [problems, setProblems] = useState<FieldProblem[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const field = (name: keyof Draft) => `${uid}-${name}`;
  const errorFor = (name: keyof Draft) =>
    problems.find((problem) => problem.path.length === 1 && problem.path[0] === name)?.message;
  const set = (changes: Partial<Draft>) => setDraft((current) => ({ ...current, ...changes }));

  async function submit(formEvent: FormEvent) {
    formEvent.preventDefault();
    setFailure(null);
    const input = { ...draft, name: draft.name.trim() };
    const found = validate(input);
    setProblems(found);
    if (found.length > 0) return;

    setPending(true);
    try {
      const made = await api.createEvent(site, {
        name: input.name,
        // The start year, as New conference names its event (D-44).
        slug: input.starts_on.slice(0, 4),
        starts_on: input.starts_on,
        ends_on: input.ends_on,
      });
      await onCreated(made);
    } catch (cause) {
      if (cause instanceof ValidationError) {
        setProblems(cause.problems);
      } else if (cause instanceof ApiError && cause.status === 403) {
        setFailure(
          "Your role on this conference cannot add an event. Ask its owner or an administrator.",
        );
      } else {
        setFailure(
          cause instanceof Error ? cause.message : "Could not save. Please try again.",
        );
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="grid gap-4" aria-label="Add its event">
      {failure && <Notice tone="danger">{failure}</Notice>}
      <ProblemSummary problems={problems} labelFor={labelFor} />

      <FieldShell label={LABELS.name} htmlFor={field("name")} required error={errorFor("name")}>
        <TextInput
          id={field("name")}
          value={draft.name}
          maxLength={300}
          invalid={Boolean(errorFor("name"))}
          onChange={(changed) => set({ name: changed.target.value })}
        />
      </FieldShell>

      <div className="grid gap-4 sm:grid-cols-2">
        <FieldShell
          label={LABELS.starts_on}
          htmlFor={field("starts_on")}
          required
          error={errorFor("starts_on")}
        >
          <TextInput
            id={field("starts_on")}
            type="date"
            value={draft.starts_on}
            invalid={Boolean(errorFor("starts_on"))}
            onChange={(changed) => set({ starts_on: changed.target.value })}
          />
        </FieldShell>
        <FieldShell
          label={LABELS.ends_on}
          htmlFor={field("ends_on")}
          required
          help="The same day as the start for a one-day event."
          error={errorFor("ends_on")}
        >
          <TextInput
            id={field("ends_on")}
            type="date"
            value={draft.ends_on}
            invalid={Boolean(errorFor("ends_on"))}
            onChange={(changed) => set({ ends_on: changed.target.value })}
          />
        </FieldShell>
      </div>

      <div>
        <Button type="submit" variant="primary" pending={pending} pendingLabel="Adding…">
          Add the event
        </Button>
      </div>
    </form>
  );
}
